import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, startAfter, getDocs, doc, getDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// UI Components
import { 
  Box, Button, Container, Grid, Paper, TextField, Typography, 
  Avatar, Card, CardContent, CardHeader, CardActions, Chip, 
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, CircularProgress, Divider, useMediaQuery, useTheme
} from '@mui/material';
import { 
  Search, FilterAlt, WhatsApp, Edit, Delete, 
  Person, Work, Assessment, Event, LocationOn, Psychology, AccessTime,
  Add as AddIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import FindProfessionalsTab from '../components/FindProfessionalsTab';
import Footer from '../components/Footer';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'all 0.3s ease',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  background: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

// Main Component
const NewParentDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchFilters, setSearchFilters] = useState({
    specialty: '',
    location: ''
  });
  const [professionals, setProfessionals] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isRequirementDialogOpen, setIsRequirementDialogOpen] = useState(false);
  const [newRequirement, setNewRequirement] = useState({
    title: '',
    description: '',
    location: '',
    preferredSkills: [],
    urgency: 'medium',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [userName, setUserName] = useState('');
  
  const auth = getAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get time-based greeting with username
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    let greeting = '';
    if (hour < 12) greeting = 'Good Morning';
    else if (hour < 18) greeting = 'Good Afternoon';
    else greeting = 'Good Evening';
    
    return `${greeting}${userName ? `, ${userName}` : ''}, you're not alone on this journey.`;
  };
  
  // Supportive message for parents
  const missionStatement = "Let's work together to get the best support for your child.";

  // Fetch initial data and user info
  useEffect(() => {
    // Get user's display name
    if (auth.currentUser) {
      const displayName = auth.currentUser.displayName || '';
      setUserName(displayName.split(' ')[0] || 'Parent'); // Use first name or fallback to 'Parent'
    }
    
    fetchInitialData();
  }, [auth.currentUser]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchProfessionals(),
        fetchRequirements(),
        fetchAssessments()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all professionals with filters
  const fetchProfessionals = async () => {
    try {
      console.log('Starting to fetch professionals with filters:', searchFilters);
      
      // Set loading state and reset professionals
      setLoading(true);
      setProfessionals([]);
      setHasMore(false);

      // Base query to get all professionals
      let q = query(
        collection(db, 'professionals')
        // Removing orderBy to see if it affects the results
      );

      console.log('Base query created');

      // Apply filters if any
      if (searchFilters.specialty) {
        q = query(q, where('expertise', 'array-contains', searchFilters.specialty));
        console.log('Applied specialty filter:', searchFilters.specialty);
      }
      if (searchFilters.location) {
        q = query(q, where('city', '==', searchFilters.location));
        console.log('Applied location filter:', searchFilters.location);
      }

      console.log('Executing Firestore query...');
      
      // Get all matching professionals
      const querySnapshot = await getDocs(q);
      console.log('Total professionals in query:', querySnapshot.size);
      
      if (querySnapshot.empty) {
        console.log('No professionals found in the query');
      } else {
        console.log('Sample professional IDs from query:', querySnapshot.docs.slice(0, 3).map(doc => doc.id));
      }
      
      // Process each professional document
      const professionalPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const docId = docSnap.id;
        
        console.log(`Processing professional [${docId}]:`, data.fullName || 'No name');
        
        if (!data.fullName) {
          console.log(`Skipping professional [${docId}] - missing fullName`);
          return null;
        }
        
        // Use photoURL from professionals collection if available
        let photoURL = data.photoURL || '';
        
        // Only fetch from users collection if photoURL is not available in professionals
        if (!photoURL) {
          console.log(`Fetching user photo for professional [${docId}]`);
          try {
            const userDoc = await getDoc(doc(db, 'users', docId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log(`Found user data for professional [${docId}]:`, userData.photoURL ? 'Has photo' : 'No photo');
              if (userData.photoURL) {
                photoURL = userData.photoURL;
              }
            } else {
              console.log(`No user document found for professional ID: ${docId}`);
            }
          } catch (err) {
            console.warn(`Error fetching user photo for professional [${docId}]:`, err);
          }
        }

        const professionalData = {
          id: docId,
          ...data,
          photoURL,
          // Ensure we have a valid updatedAt field for sorting
          updatedAt: data.updatedAt || new Date(0)
        };
        
        console.log(`Processed professional [${docId}]:`, professionalData.fullName);
        return professionalData;
      });

      console.log('Waiting for all professional data to be processed...');
      
      // Wait for all professional data to be processed and update state
      const allProfessionals = (await Promise.all(professionalPromises))
        .filter(Boolean); // Remove any null entries
      
      console.log('Total valid professionals after processing:', allProfessionals.length);
      
      // Sort by updatedAt in descending order (newest first)
      const sortedProfessionals = [...allProfessionals].sort((a, b) => {
        const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(0);
        const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(0);
        return dateB - dateA;
      });
      
      console.log('Setting professionals state with', sortedProfessionals.length, 'professionals');
      
      // Update state with all professionals
      setProfessionals(sortedProfessionals);
      
      // Show message if no professionals found
      if (sortedProfessionals.length === 0) {
        console.log('No professionals found matching criteria');
        toast.info('No professionals found matching your criteria');
      } else {
        console.log(`Successfully loaded ${sortedProfessionals.length} professionals`);
        console.log('First 3 professionals:', sortedProfessionals.slice(0, 3).map(p => p.fullName));
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
      toast.error('Failed to load professionals');
    } finally {
      setLoading(false);
    }
  };

  // Removed test and debug functions for production

  // Fetch parent's job posts
  const fetchRequirements = async () => {
    if (!auth.currentUser) {
      console.log('No authenticated user');
      return;
    }
    
    const userId = auth.currentUser.uid;
    console.log('Current user ID:', userId);
    
    try {
      // Query jobPosts collection for this parent's posts
      const q = query(
        collection(db, 'jobPosts'),
        where('posterId', '==', userId)
        // Removed orderBy to avoid needing an index for now
      );
      
      console.log('Querying jobPosts with filter:', {
        posterId: userId,
        collection: 'jobPosts'
      });
      
      const querySnapshot = await getDocs(q);
      
      console.log('Query results:', {
        size: querySnapshot.size,
        empty: querySnapshot.empty,
        docs: querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      });
      
      if (querySnapshot.empty) {
        console.log('No job posts found for user');
        setRequirements([]);
        return;
      }
      
      const jobPostsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || data.role || 'Untitled Job Post',
          description: data.description || data.currentSkill || '',
          status: data.status || 'open',
          urgency: data.urgency || 'medium',
          location: data.location?.area || data.location?.city || 'Not specified',
          postedAt: data.postedAt?.toDate() || new Date(),
          skills: data.skills || [],
          posterType: data.posterType || 'parent',
          posterEmail: data.posterEmail || '',
          applications: data.applications || []
        };
      });
      
      console.log('Processed job posts:', jobPostsData);
      setRequirements(jobPostsData);
    } catch (error) {
      console.error('Error fetching job posts:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      toast.error(`Failed to load job posts: ${error.message}`);
    }
  };

  // Fetch child's assessments
  const fetchAssessments = async () => {
    if (!auth.currentUser) return;
    
    try {
      // Fetch assessments from the root assessments collection filtered by userId
      const q = query(
        collection(db, 'assessments'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const assessmentsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.createdAt?.toDate() || new Date(),
          type: data.type || 'M-CHAT-R/F',
          status: data.status || 'completed',
          // Include any other fields you want to display
          score: data.score,
          riskLevel: data.riskLevel,
          childName: data.childName || 'Your Child'
        };
      });
      
      setAssessments(assessmentsData);
      
      if (assessmentsData.length === 0) {
        console.log('No assessments found for this user');
      } else {
        console.log(`Found ${assessmentsData.length} assessments`);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      // Don't show error toast for missing permissions, just log it
      if (error.code !== 'permission-denied') {
        toast.error('Failed to load assessments');
      } else {
        console.error('Permission denied when fetching assessments');
      }
      setAssessments([]);
    }
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfessionals(false);
  };

  // Handle deleting a job post
  const handleDeleteRequirement = async (jobPostId) => {
    if (!window.confirm('Are you sure you want to delete this job post? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'jobPosts', jobPostId));
      toast.success('Job post deleted successfully!');
      fetchRequirements(); // Refresh the list
    } catch (error) {
      console.error('Error deleting job post:', error);
      toast.error(`Failed to delete job post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle editing a job post
  const handleEditRequirement = (jobPost) => {
    setNewRequirement({
      title: jobPost.title,
      description: jobPost.description,
      location: typeof jobPost.location === 'string' ? jobPost.location : jobPost.location?.city || '',
      preferredSkills: jobPost.skills || [],
      urgency: jobPost.urgency || 'medium',
      id: jobPost.id
    });
    setIsRequirementDialogOpen(true);
  };

  // Handle requirement submission
  const handleSubmitRequirement = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) {
      toast.error('Please log in to post a requirement');
      return;
    }

    try {
      setLoading(true);
      const jobPostData = {
        title: newRequirement.title,
        description: newRequirement.description,
        status: 'open',
        urgency: newRequirement.urgency,
        location: {
          city: newRequirement.location,
          area: newRequirement.location
        },
        skills: newRequirement.preferredSkills,
        posterId: auth.currentUser.uid,
        posterEmail: auth.currentUser.email || '',
        posterType: 'parent',
        postedAt: serverTimestamp(),
        applications: []
      };
      
      console.log('Creating job post with data:', jobPostData);
      await addDoc(collection(db, 'jobPosts'), jobPostData);
      
      toast.success('Job post created successfully!');
      setNewRequirement({
        title: '',
        description: '',
        location: '',
        preferredSkills: [],
        urgency: 'medium',
      });
      setIsRequirementDialogOpen(false);
      fetchRequirements();
    } catch (error) {
      console.error('Error creating job post:', error);
      toast.error(`Failed to create job post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Format name to display first name and last initial (e.g., 'Pramod K.')
  const formatName = (fullName) => {
    if (!fullName) return '';
    
    // Split into parts and trim whitespace
    const parts = fullName.trim().split(/\s+/);
    
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0]; // Single name
    
    // Format first letter of each part to uppercase, rest lowercase
    const formattedParts = parts.map(part => 
      part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    );
    
    // Return first name + space + first letter of last name + '.'
    return `${formattedParts[0]} ${formattedParts[formattedParts.length - 1].charAt(0)}.`;
  };

  // Professional Card Component
  const ProfessionalCard = ({ professional }) => {
    console.log('Rendering professional card:', professional.id, professional.fullName);
    
    // Use photoURL from professionals collection by default, fallback to empty string
    const [profilePhoto, setProfilePhoto] = useState(professional.photoURL || '');
    
    // Format the professional's name
    const displayName = formatName(professional.fullName || '');
    
    // Fetch user's photo from users collection if not available in professional data
    useEffect(() => {
      const fetchUserPhoto = async () => {
        // Only fetch from users collection if professional.photoURL is not available
        if (!profilePhoto && professional.id) {
          console.log('Fetching photo for professional:', professional.id);
          try {
            const userDoc = await getDoc(doc(db, 'users', professional.id));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log('Found user data for professional:', professional.id, 'Photo URL:', userData.photoURL);
              if (userData.photoURL) {
                setProfilePhoto(userData.photoURL);
              }
            } else {
              console.log('No user document found for professional ID:', professional.id);
            }
          } catch (error) {
            console.error('Error fetching user photo for professional:', professional.id, error);
          }
        } else if (profilePhoto) {
          console.log('Using existing photo URL for professional:', professional.id, profilePhoto);
        }
      };
      
      fetchUserPhoto();
    }, [professional.id, professional.photoURL, profilePhoto]);
    
    // Default availability (Mon-Fri) if none provided or empty
    const defaultAvailability = {
      monday: { available: true },
      tuesday: { available: true },
      wednesday: { available: true },
      thursday: { available: true },
      friday: { available: true }
    };
    
    // Check if professional.availability is null, undefined, or empty object
    const hasValidAvailability = professional.availability && 
                               Object.keys(professional.availability).length > 0 &&
                               Object.values(professional.availability).some(day => day.available);
    
    // Use professional's availability if valid, otherwise fallback to default
    const availability = hasValidAvailability ? professional.availability : defaultAvailability;
    
    // Get professional's name from possible fields
    const professionalName = professional.fullName || professional.displayName || 
                           professional.name || professional.username || 'your profile';
    
    // Create WhatsApp message with professional's name
    const whatsappMessage = `Hello, I found ${professionalName} on ASDFlo and would like to know more about your services.`;
    
    // Create and encode the WhatsApp URL
    const whatsappBaseUrl = 'https://wa.me/918978733122';
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappLink = `${whatsappBaseUrl}?text=${encodedMessage}`;
    
    console.log('WhatsApp link:', whatsappLink); // Debug log

    return (
      <StyledCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
          <Avatar 
            src={profilePhoto} 
            alt={displayName}
            sx={{ 
              width: 72, 
              height: 72,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              fontWeight: 600,
              flexShrink: 0,
              boxShadow: 3,
              '& .MuiAvatar-img': {
                objectFit: 'cover',
                width: '100%',
                height: '100%'
              }
            }}
          >
            {displayName ? displayName.charAt(0).toUpperCase() : 'P'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.5 }}>
            <Typography variant="h6" component="div" fontWeight="700" noWrap color="text.primary">
              {displayName || 'Professional'}
            </Typography>
            {professional.expertise && (
              <Typography 
                variant="subtitle2" 
                color="text.secondary" 
                noWrap
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  mt: 0.5
                }}
              >
                {professional.expertise}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mb: 3.5, flexGrow: 1, px: 0.5 }}>
          {professional.city && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOn 
                color="action" 
                fontSize="small" 
                sx={{ 
                  mr: 1.75, 
                  color: 'text.secondary', 
                  width: 22, 
                  flexShrink: 0,
                  opacity: 0.8 
                }} 
              />
              <Typography variant="body1" color="text.primary" noWrap sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                {professional.city}
              </Typography>
            </Box>
          )}
          {professional.experience && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Work 
                color="action" 
                fontSize="small" 
                sx={{ 
                  mr: 1.75, 
                  color: 'text.secondary', 
                  width: 22, 
                  flexShrink: 0,
                  opacity: 0.8 
                }} 
              />
              <Typography variant="body1" color="text.primary" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                {typeof professional.experience === 'object' 
                  ? `${professional.experience.years || 'N/A'} years experience`
                  : `${professional.experience} years experience`
                }
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <AccessTime 
              color="action" 
              fontSize="small" 
              sx={{ 
                mr: 1.75, 
                mt: 0.5, 
                color: 'text.secondary', 
                width: 22, 
                flexShrink: 0,
                opacity: 0.8 
              }} 
            />
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" color="text.primary" sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.4,
                ml: 0.5,
                fontWeight: 500
              }}>
                  {Object.entries(availability)
                    .filter(([_, value]) => value.available)
                    .map(([day]) => {
                      // Convert full day name to 3-letter abbreviation
                      const dayMap = {
                        monday: 'Mon',
                        tuesday: 'Tue',
                        wednesday: 'Wed',
                        thursday: 'Thu',
                        friday: 'Fri',
                        saturday: 'Sat',
                        sunday: 'Sun'
                      };
                      return dayMap[day.toLowerCase()] || day.substring(0, 3);
                    })
                    .join(', ')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Button
            component="a"
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="primary"
            startIcon={<WhatsApp sx={{ fontSize: '1.25rem' }} />}
            onClick={(e) => {
              // Force the URL to be opened in a new tab with proper encoding
              e.preventDefault();
              window.open(whatsappLink, '_blank', 'noopener,noreferrer');
            }}
            sx={{
              backgroundColor: '#25D366',
              color: 'white',
              '&:hover': {
                backgroundColor: '#128C7E',
                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
              },
              '&:active': {
                transform: 'translateY(1px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              py: 1.25,
              fontSize: '1rem',
              width: '100%',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                marginLeft: '-4px'
              }
            }}
          >
            Contact Now
          </Button>
        </Box>
      </StyledCard>
    );
  };

  // Render professional card
  const renderProfessionalCard = (professional) => (
    <Grid item xs={12} sm={6} md={4} key={professional.id}>
      <ProfessionalCard professional={professional} />
    </Grid>
  );

  // Render requirement card
  const renderRequirementCard = (req) => (
    <Grid item xs={12} sm={6} md={4} key={req.id}>
      <Card>
        <CardHeader
          title={req.title}
          subheader={`Posted on ${req.postedAt?.toLocaleDateString() || 'recently'}`}
          action={
            <Box>
              <IconButton size="small" onClick={() => handleEditRequirement(req)}>
                <Edit fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDeleteRequirement(req.id)}>
                <Delete fontSize="small" color="error" />
              </IconButton>
            </Box>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {req.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip 
              size="small" 
              label={req.status || 'Open'} 
              color={req.status === 'closed' ? 'default' : 'primary'} 
              variant="outlined" 
            />
            <Chip 
              size="small" 
              label={req.urgency || 'Medium'} 
              color={
                req.urgency === 'high' ? 'error' : 
                req.urgency === 'low' ? 'success' : 'warning'
              } 
              variant="outlined" 
            />
            {req.location && (
              <Chip 
                size="small" 
                label={req.location} 
                icon={<LocationOn fontSize="small" />} 
                variant="outlined" 
              />
            )}
            {req.skills?.map((skill, index) => (
              <Chip 
                key={index} 
                size="small" 
                label={skill} 
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Posted {req.postedAt?.toLocaleDateString() || 'recently'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {req.applications?.length || 0} applications
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  // Render assessment card
  const renderAssessmentCard = (assessment) => (
    <Grid item xs={12} key={assessment.id}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <div>
              <Typography variant="h6" component="div">
                {assessment.type || 'Developmental Assessment'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {assessment.notes || 'No additional notes'}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  size="small" 
                  label={assessment.status || 'scheduled'} 
                  color={
                    assessment.status === 'completed' ? 'success' : 
                    assessment.status === 'cancelled' ? 'error' : 'primary'
                  }
                />
                <Chip 
                  size="small" 
                  variant="outlined"
                  label={new Date(assessment.date).toLocaleDateString()}
                  icon={<Event fontSize="small" />}
                />
                {assessment.professionalName && (
                  <Chip 
                    size="small" 
                    variant="outlined"
                    label={assessment.professionalName}
                    icon={<Person fontSize="small" />}
                  />
                )}
              </Box>
            </div>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => {/* View report handler */}}
            >
              View Report
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="xl" component="main" sx={{ py: 4, flex: 1 }}>
      <Box sx={{ p: isMobile ? 2 : 4, maxWidth: '1600px', mx: 'auto' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: isMobile ? 2 : 4,
          gap: 2
        }}>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography 
                variant="h6" 
                component="h2"
                sx={{ 
                  color: 'text.secondary',
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  letterSpacing: '0.02em',
                  mb: 0.5,
                  lineHeight: 1.4
                }}
              >
                {getTimeBasedGreeting()}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  fontStyle: 'italic',
                  maxWidth: '600px',
                  lineHeight: 1.4
                }}
              >
                {missionStatement}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            width: isMobile ? '100%' : 'auto'
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsRequirementDialogOpen(true)}
              sx={{
                minWidth: 'auto',
                p: isMobile ? '8px' : '8px 16px',
                borderRadius: '12px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <AddIcon sx={{ mr: isMobile ? 0 : 1 }} />
              {!isMobile && 'New Requirement'}
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 3,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-scrollable': {
                overflow: 'auto',
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
              '& .MuiTab-root': {
                minWidth: 'auto',
                padding: isMobile ? '12px 8px' : '12px 16px',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                minHeight: '48px',
                '& .MuiSvgIcon-root': {
                  marginBottom: '0 !important',
                  marginRight: '8px',
                  fontSize: isMobile ? '1rem' : '1.25rem',
                }
              },
            }}
          >
            <Tab 
              label={isMobile ? 'Professionals' : 'Find Professionals'} 
              icon={<Person fontSize={isMobile ? 'small' : 'medium'} />} 
              iconPosition="start" 
            />
            <Tab 
              label={isMobile ? 'Requirements' : 'My Requirements'} 
              icon={<Work fontSize={isMobile ? 'small' : 'medium'} />} 
              iconPosition="start" 
            />
            <Tab 
              label="Assessments" 
              icon={<Assessment fontSize={isMobile ? 'small' : 'medium'} />} 
              iconPosition="start" 
            />
          </Tabs>
        </Box>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ mt: 3 }}>
        {/* Find Professionals Tab */}
        {activeTab === 0 && (
  <FindProfessionalsTab limitResults={12} showHeader={true} headerText="Find Professionals" />
)}

        {/* My Requirements Tab */}
        {activeTab === 1 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <SectionTitle variant="h5">
                <Work sx={{ mr: 1 }} />
                My Requirements
              </SectionTitle>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={() => setIsRequirementDialogOpen(true)}
              >
                New Requirement
              </Button>
            </Box>

            <Grid container spacing={2}>
              {requirements.length > 0 ? (
                requirements.map(renderRequirementCard)
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      You haven't posted any requirements yet.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      sx={{ mt: 2 }}
                      onClick={() => setIsRequirementDialogOpen(true)}
                    >
                      Post Your First Requirement
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Assessments Tab */}
        {activeTab === 2 && (
          <>
            <SectionTitle variant="h5">
              <Assessment sx={{ mr: 1 }} />
              Child's Assessments
            </SectionTitle>

            <Grid container spacing={2}>
              {assessments.length > 0 ? (
                assessments.map(renderAssessmentCard)
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No assessments found for your child.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      sx={{ mt: 2 }}
                      onClick={() => window.location.href = '/free-assessment'}
                      component="a"
                      href="/free-assessment"
                    >
                      Schedule an Assessment
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Box>

      {/* New Requirement Dialog */}
      <Dialog 
        open={isRequirementDialogOpen} 
        onClose={() => setIsRequirementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Post New Requirement</DialogTitle>
        <form onSubmit={handleSubmitRequirement}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={newRequirement.title}
                  onChange={(e) => setNewRequirement(prev => ({ ...prev, title: e.target.value }))}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newRequirement.description}
                  onChange={(e) => setNewRequirement(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={4}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={newRequirement.location}
                  onChange={(e) => setNewRequirement(prev => ({ ...prev, location: e.target.value }))}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Urgency"
                  value={newRequirement.urgency}
                  onChange={(e) => setNewRequirement(prev => ({ ...prev, urgency: e.target.value }))}
                  margin="normal"
                  SelectProps={{ native: true }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Preferred Skills (comma separated)"
                  value={newRequirement.preferredSkills.join(', ')}
                  onChange={(e) => setNewRequirement(prev => ({
                    ...prev,
                    preferredSkills: e.target.value.split(',').map(skill => skill.trim()).filter(Boolean)
                  }))}
                  margin="normal"
                  helperText="e.g., ABA Therapy, Speech Therapy, Occupational Therapy"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setIsRequirementDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Post Requirement'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      </Container>
      <Footer />
    </Box>
  );
};

export default NewParentDashboard;
